import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { C } from "../lib/colors.js";
import { ModalShell, CloseButton } from "./Panels.jsx";
import { sameSourceEvent } from "../lib/forecast/derivedBranch.js";
import { SECTION_LABEL_STYLE, SECTION_RULE } from "../lib/preprocessPanelStyles.js";
import { ui } from "../lib/telemetry/instrument.js";
import { numberedSourceLabels, sourceNameOf } from "../lib/report/sourceLabel.js";
import { IconChevronRight } from "./ui/Icons.jsx";

// Branch-scenario icon, echoing the timeline's resolution markers: a DOTTED spine,
// a branch dropping DOWN and turning horizontal, terminating in an UNFILLED circle
// holding a check (`mark="check"` → the trigger OCCURS) or an X (`mark="x"` → does
// NOT). Spine, branch, circle, and mark all share the one gold `color`; the branch
// line is split around the circle so it never shows through the outline.
function TriggerBranchIcon({ mark, size = 22, color = C.action }) {
  return (
    <svg
      width={size * 2}
      height={Math.round((size * 2 * 40) / 48)}
      viewBox="0 0 48 40"
      fill="none"
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      {/* dotted top spine — runs the full width, continuing past the branch fork */}
      <path
        d="M2 6 H46"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="0.5 3.5"
      />
      {/* dotted branch — forks DOWN (dropped lower to clear the circle), turns horizontal.
        Split into a left stub (up to the circle's edge) and a right run (resuming past it)
        so the dotted line never shows THROUGH the unfilled circle. */}
      <path
        d="M10 6 V26 H13"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="0.5 3.5"
      />
      <path
        d="M35 26 H46"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="0.5 3.5"
      />
      {/* outcome circle on the branch — OUTLINE only (unfilled); the gold check (occurs)
        / X (does not) is the fill */}
      <circle cx="24" cy="26" r="9.9" stroke={color} strokeWidth="2" fill="none" />
      {mark === "check" ? (
        <polyline
          points="19,26.4 22.7,30 29.4,21"
          fill="none"
          stroke={color}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <>
          <line
            x1="18.4"
            y1="20.4"
            x2="29.6"
            y2="31.6"
            stroke={color}
            strokeWidth="2.4"
            strokeLinecap="round"
          />
          <line
            x1="29.6"
            y1="20.4"
            x2="18.4"
            y2="31.6"
            stroke={color}
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </>
      )}
    </svg>
  );
}

/** Compact marker for a user trigger whose coherent triple was unavailable. */
export function TriggerScoreBadge() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        border: `1px solid ${C.action}`,
        color: C.action,
        borderRadius: 999,
        padding: "1px 6px",
        fontSize: 11,
        lineHeight: 1.4,
        letterSpacing: 0,
      }}
    >
      Not scored
    </span>
  );
}

export function Tooltip({ ev, x, y, today = -Infinity, zoom = 1 }) {
  // This box is position:fixed but renders INSIDE the root div's CSS `zoom` frame,
  // so the browser multiplies its left/top by `zoom`. Convert the incoming viewport
  // coords (raw clientX/Y) and the viewport-size clamps into that zoomed frame by
  // dividing by `zoom`, so the box lands under the cursor at any Display size.
  // At zoom=1 this is a no-op.
  x = x / zoom;
  y = y / zoom;
  const vpW = window.innerWidth / zoom;
  const vpH = window.innerHeight / zoom;
  const pEvt = ev.p_event != null ? ev.p_event : ev.probability;
  const dateFmt = (d, opts) => new Date(d).toLocaleDateString("en-US", opts);
  const hasRange = ev.date_end && ev.date_end !== ev.date;
  // A FUTURE event's window can't start before today — clamp the displayed start
  // so the tooltip agrees with the timeline (which renders the circle + its
  // distribution no earlier than today). `today` (effectiveToday ms) is passed by
  // the caller; default -Infinity is a true Math.max no-op for callers that don't clamp.
  const startMs = ev.isPast
    ? new Date(ev.date).getTime()
    : Math.max(new Date(ev.date).getTime(), today);
  const dateLabel = (() => {
    if (!hasRange) return dateFmt(startMs, { month: "short", year: "numeric" });
    const de = new Date(ev.date_end);
    if (de.getTime() <= startMs) return dateFmt(startMs, { month: "short", year: "numeric" });
    const ds = new Date(startMs);
    const sameYM = ds.getFullYear() === de.getFullYear() && ds.getMonth() === de.getMonth();
    if (sameYM)
      return `${dateFmt(startMs, { month: "short", day: "numeric" })}–${de.getDate()}, ${de.getFullYear()}`;
    return `${dateFmt(startMs, { month: "short", year: "numeric" })} — ${dateFmt(ev.date_end, { month: "short", year: "numeric" })}`;
  })();
  // Source links only on OCCURRED (past) triggers — a future trigger's citation is
  // just the evidence the model reasoned FROM, and showing it reads as if the event
  // already happened. Deriving `urls` empty for future triggers ALSO drops the
  // tooltip's `pointerEvents:auto` (line below) so a future tooltip with a populated
  // source_url doesn't keep intercepting pointer events with nothing to click.
  const urls =
    ev.isPast && ev.source_url
      ? Array.isArray(ev.source_url)
        ? ev.source_url
        : [ev.source_url]
      : [];
  const urlLabels = numberedSourceLabels(urls);
  // tw = the tooltip's FULL rendered width for the flip/clamp math: content
  // width (230) + horizontal padding (2×12) + border (2×1) = 256. The box is
  // content-box (no border-box reset), so using the content width alone let the
  // right-edge clamp under-count and overflow the viewport by a few px.
  const tw = 256,
    th = 160;
  // Triggers have CLEAR/COLLAPSE buttons that extend ~120px to the right of the cursor — flip
  // the tooltip to the LEFT so it doesn't paint over them. Falls back to right-of-cursor if
  // the left edge would clip off-screen.
  const trigLeft = ev._isTrigger ? x - tw - 44 : null;
  // FINAL FORECAST (resolution) tooltip: the resolution pie sits at the right edge
  // near the chat console, so the default right-of-cursor placement tucks it under
  // the panel and can cover the pie's %. Place it just LEFT of the cursor with a
  // tight ~6px gap (reads as attached to the mouse) and only a small downward
  // nudge to clear the % readout. Falls back to right-of-cursor only if the left
  // edge would clip.
  const resLeft = ev.isResolution && x - tw - 6 >= 8 ? x - tw - 6 : null;
  let tx = resLeft ?? (trigLeft != null && trigLeft >= 8 ? trigLeft : x + 44),
    ty = y + 12;
  if (tx + tw > vpW - 8) tx = x - tw - (ev.isResolution ? 6 : 44);
  if (tx < 8) tx = 8;
  if (ty + th > vpH - 8) ty = Math.max(8, vpH - th - 8);
  // While a trigger is hovered the distribution block at the resolution
  // line previews it (MultiwayMarker `data-previewing`); a trigger near that line would put this
  // box over the preview, so it goes left of the cursor instead. Measured after commit, before
  // paint, against the placement it would otherwise take.
  const boxRef = useRef(null);
  const placedTx = tx;
  const isResolution = !!ev.isResolution;
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const block = isResolution ? null : document.querySelector("[data-previewing]");
    let over = false;
    if (block) {
      const b = block.getBoundingClientRect();
      const left = placedTx * zoom;
      const top = ty * zoom;
      over =
        left < b.right &&
        left + tw * zoom > b.left &&
        top < b.bottom &&
        top + el.offsetHeight * zoom > b.top;
    }
    el.style.left = `${over ? Math.max(8, x - tw - 44) : placedTx}px`;
  }, [isResolution, placedTx, ty, tw, x, zoom]);
  return (
    <div
      ref={boxRef}
      style={{
        position: "fixed",
        left: tx,
        top: ty,
        zIndex: 500,
        background: C.tooltipBg,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: "10px 12px",
        width: 230,
        pointerEvents: urls.length ? "auto" : "none",
        boxShadow: C.shadowBox,
        ...(ev.isResolution ? { overflow: "hidden" } : {}),
      }}
    >
      {ev.isResolution && (
        <div
          style={{
            background: ev.isPast ? C.gray : C.accent,
            color: C.tooltipBg,
            fontWeight: 600,
            fontSize: 12,
            textAlign: "center",
            padding: "7px 12px",
            margin: "-10px -12px 9px",
          }}
        >
          Final forecast
        </div>
      )}
      {!ev.isResolution && (
        <>
          <div
            style={{
              fontSize: 12,
              color: ev.isPast ? C.gray : C.accent,
              letterSpacing: 0,
              marginBottom: 2,
            }}
          >
            {/* Multi-value triggers carry no per-event probability (pEvt null) — show the
                label + date without a meaningless "NaN%". */}
            {ev.isPast
              ? "Past trigger"
              : Number.isFinite(pEvt)
                ? `${Math.round(pEvt * 100)}% chance it happens`
                : "Possible trigger"}
          </div>
          <div
            style={{
              fontSize: 12,
              color: ev.isPast ? C.gray : C.accent,
              letterSpacing: 0,
              marginBottom: 4,
            }}
          >
            {dateLabel}
          </div>
          {/* What is known about whether the trigger happened, when a scenario hangs on it:
              the node says it with its ring and "?", but at ~14px that is a hint, not a
              statement; a user asking "its date has passed, why no ✓?" is hovering, and this
              is where the answer has room to be words. Watching and overdue are only ever
              set for a premise that is actually gradable (see ForecastLane's triggerCtx), so
              they never promise a verdict that cannot come. */}
          {ev._scenarioOutcome && (
            <div
              style={{
                fontSize: 11,
                color: ev._scenarioOutcome === "undetermined" ? C.grayL : C.purpleL || C.accent,
                letterSpacing: 0,
                marginBottom: 4,
              }}
            >
              {ev._scenarioOutcome === "watching"
                ? "Waiting to see whether it happened"
                : ev._scenarioOutcome === "overdue"
                  ? "Its dates have passed with no ruling yet"
                  : "Sayer couldn't tell whether it happened"}
              {ev._scenarioOutcome === "overdue" && ev._recheckable && (
                <div>Sayer checks again at each update</div>
              )}
            </div>
          )}
          {ev._isTrigger && ev._scoreStatus && ev._scoreStatus !== "coherent" ? (
            <div style={{ marginBottom: 4 }}>
              <TriggerScoreBadge />
            </div>
          ) : null}
        </>
      )}
      <div
        style={{
          fontSize: 13,
          fontWeight: "bold",
          color: C.white,
          marginBottom: 5,
          lineHeight: 1.35,
        }}
      >
        {ev.title}
      </div>
      {/* WINNER, restated next to the question it answers — the bars are small and sit far
          out on the timeline. Set only by call sites that draw a distribution, so every other
          tooltip is untouched.
          
          Laid out as PERCENT then LABEL-to-the-right, in the winner's white treatment: the
          same reading order and emphasis the winning row has on the timeline, so the tooltip
          and the bars state the outcome the same way. It used to stack the label above the
          number, in the accent colour, which read as two separate facts.
          
          The label wraps rather than shrinks — sizing it to fit would defeat stating it
          large. */}
      {ev.isResolution && ev._winner && (
        <div style={{ marginTop: 7, marginBottom: 2 }}>
          {/* Centred, matching the "Click to view the full analysis" line below it — the
              question above is left-aligned prose, but the outcome is the tooltip's headline
              and reads as a centred statement rather than a continuation of the question. */}
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                fontSize: 29,
                fontWeight: "bold",
                lineHeight: 1.1,
                color: C.white,
                fontFamily: '"IBM Plex Mono",monospace',
              }}
            >
              {ev._winner.pctText}
            </span>
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.25,
                color: C.white,
              }}
            >
              {ev._winner.label}
            </span>
          </div>
          {/* "Most likely" is dropped: being stated as the winner already says it, which is
              exactly what made it redundant. A SETTLED badge ("Resolved", "Tied") is kept —
              that is not implied by anything on show, and losing it would leave a resolved
              forecast's tooltip indistinguishable from a live one. */}
          {ev._winner.badge && ev._winner.badge !== "Most likely" && (
            <div
              style={{
                marginTop: 2,
                fontSize: 12,
                textAlign: "center",
                color: C.grayL,
              }}
            >
              {ev._winner.badge}
            </div>
          )}
        </div>
      )}
      {ev.isResolution ? (
        <div
          style={{
            fontSize: 12,
            color: C.grayL,
            lineHeight: 1.55,
            marginTop: 8,
            marginBottom: 8,
            textAlign: "center",
          }}
        >
          Click to view the full analysis
        </div>
      ) : (
        // `anywhere`: the appended source is often a bare URL, which has no break opportunity.
        <div style={{ fontSize: 12, color: C.grayL, lineHeight: 1.55, overflowWrap: "anywhere" }}>
          {ev.description}
          {/* Source/citation only on OCCURRED (past) triggers — a future trigger's
              citation is just the evidence the model reasoned FROM, and showing it
              reads as if the event already happened. */}
          {ev.isPast && ev.source ? ` — ${sourceNameOf(ev.source) || ev.source}` : ""}
        </div>
      )}
      {urls.length > 0 /* empty for future triggers — see the urls derivation above */ && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            borderTop: `1px solid ${C.border}`,
            paddingTop: 5,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {urls.map((u, i) => (
            <a
              {...ui("trigger.source-link")}
              data-telemetry-redact=""
              className="overlay-source-link"
              key={i}
              href={u}
              target="_blank"
              rel="noopener noreferrer"
            >
              {urlLabels.get(u) || (u.length > 50 ? u.slice(0, 50) + "…" : u)}
            </a>
          ))}
        </div>
      )}
      {!ev.isResolution && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            color: C.grayL,
            borderTop: `1px solid ${C.border}`,
            paddingTop: 5,
            lineHeight: 1.7,
          }}
        >
          {/* Glyph key, encodings only. The valence colour key (Increases / Decreases /
              Occurred) was dropped along with the trigger colours themselves — it named a
              distinction the timeline no longer draws. */}
          Slice shows the chance · size shows the impact
        </div>
      )}
      {/* CALIBRATION DISPLAY — decoupled from the WIP Forecast Tracking
    surface (#884): it renders for every user on a resolution
    tooltip. Call sites that render a resolution tooltip
    (`isResolution:true`) set `_calibrationEnabled` to whether real
    calibration data is present (calibCell?.resolvedTrue != null), so
    the line only shows once the Observatory snapshot has loaded and
    the bin carries a resolved-true count — no placeholder row. */}
      {ev.isResolution &&
        ev._calibrationEnabled &&
        (() => {
          const prob = Math.max(0, Math.min(1, pEvt));
          // Bucket into 10-pt bins. Clamp prob to [0,1] first (matching the call sites'
          // bin lookup, so the displayed range agrees with the bin the server picked),
          // then treat p===1 as the 90–100% bucket (not 100–110%) — lookupBin's last
          // bin is right-closed.
          const loRaw = Math.floor(prob * 10) * 10;
          const lo = Math.min(loRaw, 90);
          const hi = lo + 10;
          const n = ev.calibration?.n;
          const tru = ev.calibration?.resolvedTrue;
          // Defensive: render only with BOTH counts present, so a partial-schema or
          // future call site that flips `_calibrationEnabled` without `n` can't throw
          // on `.toLocaleString()`. No placeholder row — hide the line cleanly.
          if (tru == null || n == null || n === 0) return null;
          const pct = ((tru / n) * 100).toFixed(1);
          return (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: C.grayL,
                borderTop: `1px solid ${C.border}`,
                paddingTop: 5,
                lineHeight: 1.6,
              }}
            >
              Across Sayer&apos;s resolved forecasts,{" "}
              <strong style={{ color: C.white }}>{pct}%</strong> of{" "}
              <strong style={{ color: C.white }}>{n.toLocaleString()}</strong> forecasts in the{" "}
              <strong style={{ color: C.white }}>
                {lo}%–{hi}%
              </strong>{" "}
              range occurred.
              <div style={{ marginTop: 6, fontSize: 11, color: C.gray, lineHeight: 1.5 }}>
                The history is not necessarily representative of this question.
              </div>
            </div>
          );
        })()}
    </div>
  );
}

// Hover card for a report citation: the source label + its itemized quotes.
// Mirrors the timeline Tooltip's styling/placement. The quotes used to live in
// a native `title` tooltip, which was so inconspicuous that one source backing
// several points read as duplicate links — this surfaces them prominently.
// pointerEvents:none so the card never steals the hover from the link.
export function CitationCard({ label, quotes, url, x, y, zoom = 1 }) {
  // See Tooltip: position:fixed inside the root's CSS `zoom` frame, so divide the
  // incoming viewport coords + size clamps by `zoom` (no-op at zoom=1).
  x = x / zoom;
  y = y / zoom;
  const vpW = window.innerWidth / zoom;
  const vpH = window.innerHeight / zoom;
  const qs = quotes && quotes.length ? quotes : [];
  // content-box (see Tooltip): derive the FULL rendered width from the content
  // width so the flip/clamp matches the actual box. cw is the content width used
  // for the style below; tw adds horizontal padding (2×12) + border (2×1).
  const cw = 276;
  const tw = cw + 26,
    th = Math.min(74 + qs.length * 44, 320);
  let tx = x + 16,
    ty = y + 14;
  if (tx + tw > vpW - 8) tx = Math.max(8, x - tw - 16);
  if (ty + th > vpH - 8) ty = Math.max(8, vpH - th - 8);
  return (
    <div
      style={{
        position: "fixed",
        left: tx,
        top: ty,
        zIndex: 500,
        background: C.tooltipBg,
        border: `1px solid ${C.purple}`,
        borderRadius: 10,
        padding: "10px 12px",
        width: cw,
        boxShadow: C.shadowBox,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: "bold",
          color: C.purple,
          marginBottom: 6,
          letterSpacing: 0,
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          maxHeight: 220,
          overflowY: "auto",
        }}
      >
        {qs.map((q, i) => (
          <div key={i} style={{ fontSize: 12, color: C.grayL, lineHeight: 1.5 }}>
            {qs.length > 1 && <span style={{ color: C.gray, fontWeight: "bold" }}>{i + 1}. </span>}"
            {q}"
          </div>
        ))}
      </div>
      {url && (
        <div
          style={{
            marginTop: 6,
            paddingTop: 5,
            borderTop: `1px solid ${C.border}`,
            fontSize: 11,
            color: C.purple,
            wordBreak: "break-all",
            lineHeight: 1.4,
          }}
        >
          {url.length > 60 ? url.slice(0, 60) + "…" : url}
        </div>
      )}
    </div>
  );
}

export function EventModal({
  ev,
  onClose,
  onBranch,
  branches,
  sourceRunId,
  today = -Infinity,
  // (ev, sourceRunId) => void, called once per opened trigger while the scenario cards are
  // on screen, so the branch layer can start what a derivation reads ahead of the click.
  onOpen = null,
}) {
  const cancelledRef = useRef(false);
  const busy = false;
  // Trigger contract starts COLLAPSED — only the "TRIGGER CONTRACT" header label
  // shows; the user clicks it to expand the question + resolution criteria + resolves-by.
  const [contractOpen, setContractOpen] = useState(false);
  const go = async (t, v) => {
    cancelledRef.current = false;
    onBranch(ev, t, v, cancelledRef);
    onClose();
  };
  const mDateFmt = (d, opts) => new Date(d).toLocaleDateString("en-US", opts);
  const mHasRange = ev.date_end && ev.date_end !== ev.date;
  // Clamp a future event's displayed start to today (see Tooltip) so the modal's
  // date range agrees with the timeline. Default today=-Infinity → true no-op for
  // callers that don't pass it.
  const mStartMs = ev.isPast
    ? new Date(ev.date).getTime()
    : Math.max(new Date(ev.date).getTime(), today);
  const mLong = { year: "numeric", month: "long", day: "numeric" };
  const mDateLabel =
    mHasRange && new Date(ev.date_end).getTime() > mStartMs
      ? `${mDateFmt(mStartMs, mLong)} — ${mDateFmt(ev.date_end, mLong)}`
      : mDateFmt(mStartMs, mLong);
  // Source links only on OCCURRED (past) triggers (same rationale as the tooltip's
  // `urls`) — empty for future triggers so the citation list stays hidden.
  const mUrls =
    ev.isPast && ev.source_url
      ? Array.isArray(ev.source_url)
        ? ev.source_url
        : [ev.source_url]
      : [];
  const mUrlLabels = numberedSourceLabels(mUrls);
  // Hide the "Trigger occurs" / "Trigger does not occur" card if a branch already exists for that
  // action on THIS trigger. Event ids are unique only within a spine, and a reforecast may reuse an
  // id for a different contract. Match both the owning parent and the frozen semantic identity
  // stamped at branch creation. Legacy forks without that identity fail closed, so both actions stay
  // visible rather than suppressing one for an unrelated trigger.
  const branchList = branches || [];
  const forkedOffThis = (b) => b.parentRunId === sourceRunId && sameSourceEvent(ev, b.forkEvent);
  const hasOccurs = branchList.some(
    (b) => forkedOffThis(b) && (b.forkEvent.p_event ?? b.forkEvent.probability) === 1.0,
  );
  const hasNotOccurs = branchList.some(
    (b) => forkedOffThis(b) && (b.forkEvent.p_event ?? b.forkEvent.probability) === 0.0,
  );
  const showBranchSection =
    ev._live !== true && !ev.isPast && !busy && (!hasOccurs || !hasNotOccurs);
  // Announce the trigger while the user reads the cards (a storage read starts, nothing
  // else). Skipped when no card is offered — there is nothing to derive then.
  useEffect(() => {
    if (onOpen && showBranchSection) onOpen(ev, sourceRunId);
  }, [onOpen, ev, sourceRunId, showBranchSection]);
  // MAKE THIS THE FORECAST button passes `${ev.title} by ${mDateLabel}` (rather
  // than the bare `ev.title`) so that:
  //   - submit()'s timeframe-required guard (the regex that looks for a year /
  //     month / date token in the question) passes — the bare title rarely
  //     contains one, so without the appended date the guard rejected the
  //     click, populated the chat box with the title, and asked the user to
  //     "add a timeframe". That's the bug this annotation guards against.
  //   - the /preprocess endpoint has the event's date in the prompt and can
  //     turn the title into a well-formed question pinned to that date.
  // mDateLabel is the same human-readable date the modal already renders.
  return (
    <ModalShell
      onClose={onClose}
      surface="trigger"
      surfaceKey={sourceRunId}
      // The preprocess panel's look: roomy padding, sections split by rules.
      panelStyle={{
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: "28px 32px",
        width: 640,
        maxWidth: "92vw",
        boxSizing: "border-box",
      }}
    >
      <CloseButton
        dataPopupCancel
        onClick={onClose}
        style={{
          position: "absolute",
          top: 22,
          right: 26,
          fontSize: 17,
          lineHeight: 1,
          padding: "0 2px",
          zIndex: 2,
        }}
      />
      <div
        style={{
          ...SECTION_LABEL_STYLE,
          letterSpacing: 0,
          color: ev.isPast ? C.gray : C.grayL,
          marginBottom: 12,
          paddingRight: 28,
        }}
      >
        {(() => {
          const pct = Math.round(((ev.p_event != null ? ev.p_event : ev.probability) || 0) * 100);
          // A FUTURE trigger reads as a probability of occurring over its window;
          // a PAST one keeps the "Historical" framing (it already happened / didn't).
          return ev.isPast ? (
            <>Past trigger · {mDateLabel}</>
          ) : (
            <>
              <span className="sy-num" style={{ color: C.white }}>
                {pct}%
              </span>{" "}
              chance it happens · {mDateLabel}
            </>
          );
        })()}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 18,
          lineHeight: 1.45,
          letterSpacing: 0,
          color: C.white,
          marginBottom: 10,
        }}
      >
        <span>{ev.title}</span>
        {ev._scoreStatus && ev._scoreStatus !== "coherent" ? <TriggerScoreBadge /> : null}
      </div>
      <div
        style={{
          fontSize: 13,
          color: C.gray,
          lineHeight: 1.6,
          letterSpacing: 0,
          marginBottom: 4,
          overflowWrap: "anywhere",
        }}
      >
        {ev.description}
        {/* Source/citation only on OCCURRED (past) triggers — see the tooltip note. */}
        {ev.isPast && ev.source ? ` — ${sourceNameOf(ev.source) || ev.source}` : ""}
      </div>
      {mUrls.length > 0 /* empty for future triggers — see the mUrls derivation above */ && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 3 }}>
          {mUrls.map((u, i) => (
            <a
              {...ui("trigger.source-link")}
              data-telemetry-redact=""
              className="overlay-source-link"
              key={i}
              href={u}
              target="_blank"
              rel="noopener noreferrer"
            >
              {mUrlLabels.get(u) || (u.length > 50 ? u.slice(0, 50) + "…" : u)}
            </a>
          ))}
        </div>
      )}
      {showBranchSection && (
        <div>
          <div style={SECTION_RULE} />
          <div style={{ fontSize: 15, fontWeight: 600, color: C.white, marginBottom: 4 }}>
            Explore a scenario
          </div>
          <div style={{ fontSize: 13, color: C.gray, lineHeight: 1.5, marginBottom: 12 }}>
            See how the forecast would change if this trigger happens, or if it doesn&apos;t.
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "stretch" }}>
            {/* Card buttons: icon (diagonal trend arrow) + action title + a
              trigger-specific description. Layout preserved when a branch already
              exists for one side via a hidden same-width placeholder. */}
            {hasOccurs ? (
              <div className="event-branch-placeholder" aria-hidden="true" />
            ) : (
              <button
                className="event-branch-card"
                onClick={() => go("probability", 1.0)}
                {...ui("trigger.branch", "occurs")}
              >
                <TriggerBranchIcon mark="check" size={22} color={C.action} />
                <span className="event-branch-card-title">If this happens</span>
              </button>
            )}
            {hasNotOccurs ? (
              <div className="event-branch-placeholder" aria-hidden="true" />
            ) : (
              <button
                className="event-branch-card"
                onClick={() => go("probability", 0.0)}
                {...ui("trigger.branch", "not-occurs")}
              >
                <TriggerBranchIcon mark="x" size={22} color={C.action} />
                <span className="event-branch-card-title">If it doesn&apos;t happen</span>
              </button>
            )}
          </div>
        </div>
      )}
      {!ev.isPast && ev.question && ev.resolution_criteria && ev.resolution_time ? (
        // Trigger contract — placed BELOW the scenario cards. Contract-mode triggers
        // carry their own standalone contract (gated on the full question + criteria +
        // resolution_time trio, matching the mint filter). Collapsed by default: only
        // the gold "TRIGGER CONTRACT" label + a caret on the right show; the full
        // question and resolution stay HIDDEN until the user clicks to expand.
        // Borderless.
        <div>
          <div style={SECTION_RULE} />
          <div
            {...ui("trigger.contract-toggle")}
            role="button"
            tabIndex={0}
            aria-expanded={contractOpen}
            onClick={() => setContractOpen((o) => !o)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setContractOpen((o) => !o);
              }
            }}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 500, color: C.grayL }}>
              {ev.contract_key
                ? "How this trigger is decided · its own question"
                : "How this trigger is decided"}
            </span>
            <span
              aria-hidden="true"
              style={{
                color: C.gray,
                lineHeight: 0,
                display: "inline-flex",
                transform: contractOpen ? "rotate(90deg)" : "none",
                transition: "transform .15s ease",
              }}
            >
              <IconChevronRight width={16} height={16} />
            </span>
          </div>
          {contractOpen && (
            <div style={{ marginTop: 12, letterSpacing: 0 }}>
              <div style={{ fontSize: 15, color: C.white, lineHeight: 1.45, marginBottom: 6 }}>
                {ev.question}
              </div>
              {ev.contract_key ? (
                <div style={{ fontSize: 12, color: C.gray, marginBottom: 6 }}>
                  Sayer will check whether this happened.
                </div>
              ) : null}
              {ev.resolution_criteria ? (
                <div style={{ fontSize: 13, color: C.gray, lineHeight: 1.6 }}>
                  {ev.resolution_criteria}
                </div>
              ) : null}
              {ev.resolution_time ? (
                <div style={{ fontSize: 12, color: C.gray, marginTop: 6 }}>
                  Resolves by {ev.resolution_time}
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </ModalShell>
  );
}
